import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import GiftCard from "../../organism/gift-card/GiftCard";
import classes from "./PointExchange.module.scss";
import clapping from "../../static/images/character/clapping.png";
import { fetchUser, postUserGiftEmailAuth } from "../../api/UsersApi";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import { fetchGift, Gift } from "../../api/GiftApi";
import { postUserPointHistory } from "../../api/UserPointHistoryApi";
import TextForm, { TextFormResult } from "../../atoms/text-form/TextForm";
import { validators } from "../../common/utility/validator.util";

type PointExchangeLocationState = {
  pointOwnerId?: number;
  availableGiftPoint?: number;
};

const PointExchange = () => {
  const userApi = fetchUser();
  const giftApi = fetchGift();
  const postPointApi = postUserPointHistory();
  const postGiftEmailAuthApi = postUserGiftEmailAuth();
  const [emailInput, setEmailInput] = useState("");
    
  const { giftId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pointExchangeState = location.state as PointExchangeLocationState | null;

  useEffect(() => {
    userApi.fetch();
    if (giftId) {
      giftApi.fetch({ giftId });
    }
  }, [giftId]);

  const gift: Gift | undefined = giftApi.state?.data?.[0]?.giftList?.[0];
  const userEmail = userApi.state?.data?.[0]?.email ?? "";
  const hasUserEmail = userEmail.trim().length > 0;
  const pointOwnerId = pointExchangeState?.pointOwnerId;
  const currentPoint = pointExchangeState?.availableGiftPoint ?? userApi.state?.data?.[0]?.currentPoint ?? 0;
  const isGiftEmailAuthPosting = postGiftEmailAuthApi.state?.status === "processing";
  const canExchange = hasUserEmail || emailInput.trim().length > 0;
  const mailValidator = (value: string) => {
    const required = validators.required(value);
    if (!required.status) return required;
    return validators.mail(value);
  };

  const handleExchange = () => {
    if (!gift?.giftId) return;
    if (!hasUserEmail) {
      if (!emailInput.trim()) return;
      postGiftEmailAuthApi.post({
        giftId: gift.giftId,
        emailAddress: emailInput.trim(),
        pointOwnerId,
      });
      return;
    }
    postPointApi.post({
      giftId: gift.giftId,
      pointOwnerId,
    });
  };

  useEffect(() => {
    if (!postPointApi.state?.data) return;
    if (postPointApi.state?.data != null && postPointApi.state?.status === "success") {
      postPointApi.reset();
      navigate("/point-result");
    }
  }, [postPointApi.state?.data]);

  useEffect(() => {
    postGiftEmailAuthApi.reset?.();
  }, []);

  return (
    <div className={classes.container}>
      {!gift ? (
        <p>該当するギフトがありません</p>
      ) : (
        <>
          <GiftCard
            image={gift.giftImage}
            title={gift.giftName}
            point={gift.requiredPoint}
            effectiveFrom={gift.effectiveFrom}
            effectiveTo={gift.effectiveTo}
            showDisclaimer={true}
          />
          {/* ここから残りの表示 */}
          <div className={classes.pointArea}>
            <div className={classes.pointRow}>
              <span className={classes.label}>ごほうびに交換できるポイント</span>
              <span className={classes.value}>
                {currentPoint.toLocaleString("ja-JP")} pt
              </span>
            </div>
            <div className={classes.pointRow}>
              <span className={classes.label}>今回交換するポイント</span>
              <span className={classes.value}>{gift.requiredPoint.toLocaleString("ja-JP")} pt</span>
            </div>
            <div className={classes.pointRow}>
              <span className={classes.label}>交換後の残りポイント</span>
              <span className={classes.value}>
                {(currentPoint - gift.requiredPoint).toLocaleString("ja-JP")} pt
              </span>
            </div>
          </div>
          <div className={classes.actionArea}>
            <div className={classes.messageBox}>
              <img src={clapping} alt="ロゴ" className={classes.logo} />
              <div className={classes.textArea}>
                <div className={classes.line1}>
                  <span className={classes.point}>{gift.requiredPoint.toLocaleString("ja-JP")}pt</span>を使って
                </div>
                <div className={classes.line2}>ごほうびと交換してよろしいですか？</div>
              </div>
            </div>
            <div className={classes.emailArea}>
              {hasUserEmail ? (
                <div className={classes.emailMessage}>
                  <div>登録されているメールアドレスに</div>
                  <div>{gift.giftName}</div>
                  <div>を送信します</div>
                  <div className={classes.registeredEmail}>{userEmail}</div>
                </div>
              ) : (
                <>
                  <div className={classes.emailMessage}>
                    <div>{gift.giftName}</div>
                    <div>を送信するため、</div>
                    <div>メールアドレスを登録してください。</div>
                  </div>
                  <TextForm
                    name="gift-email"
                    label="メールアドレス"
                    type="text"
                    value={emailInput}
                    placeholder="sample@miraii"
                    color="white"
                    inputWidth="100%"
                    validator={mailValidator}
                    onChange={(args: TextFormResult) => setEmailInput(args.value)}
                  />
                  {postGiftEmailAuthApi.state?.status === "success" && (
                    <div className={classes.emailSentMessage}>
                      入力されたメールアドレスに認証メールを送信しました。
                    </div>
                  )}
                </>
              )}
            </div>
            <SimpleButton
              label={isGiftEmailAuthPosting ? "送信中..." : `${gift.requiredPoint.toLocaleString("ja-JP")}pt 使ってごほうびをもらう`}
              width="90%"
              color="orange"
              isRadius={true}
              onClick={handleExchange}
              disabled={!canExchange || isGiftEmailAuthPosting}
            />
            <SimpleButton
              label="ちょっと考えなおす（元の画面に戻る）"
              width="90%"
              color="gray"
              isRadius={true}
              onClick={() => navigate("/point")}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PointExchange;
